import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { createService, getSchemeServiceTypes, getServiceSubtypes, updateServiceComprehensive, sendLocationSharingMessage, checkForLocationUpdate, getDealers } from '../../services/addService.service';
import { runInLane } from '../../utils/requestLane';
import AddressAutocomplete from './AddressAutocomplete';
import { formatDateTime } from '../../utils/dateUtils';
import { getCurrentPatronId } from '../../utils/brandingUtils';
import { calculatePickupToDropDistance, isGoogleMapsLoaded } from '../../utils/distanceUtils';
import { X, ListTree, Plus } from 'lucide-react';
import { Section } from './workspacePrimitives';
import TriagePanel from '../diagnostics/TriagePanel';

const MANUAL_LOCATION_REASONS = [
  { value: '', label: '- Select Reason -' },
  { value: 'customer_not_at_location', label: 'Customer not at location' },
  { value: 'location_not_accessible', label: 'Location not accessible' },
  { value: 'customer_requested_different_location', label: 'Customer requested different location' },
  { value: 'other', label: 'Other' },
];

const CaseServicesSegment = ({ services, onSelectService, selectedService, detailedServiceData, contactNumber, caseId, schemeId, onServiceAdded, onServiceCreated, loadingServices, caseObj, onServiceTypesLoaded }) => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingServiceData, setPendingServiceData] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Location polling state
  const [pollingForLocation, setPollingForLocation] = useState(false);
  const [locationFound, setLocationFound] = useState(false);
  const [pollingTimer, setPollingTimer] = useState(0);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  
  // New state for service types and subtypes
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceSubtypes, setServiceSubtypes] = useState([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  const [loadingServiceSubtypes, setLoadingServiceSubtypes] = useState(false);
  
  // New state for dealer selection
  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [useDealerForDropLocation, setUseDealerForDropLocation] = useState(false); // Default to manual entry
  
  // Distance calculation state
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [distanceError, setDistanceError] = useState('');
  
  // Get current patron ID for conditional rendering
  const currentPatronId = getCurrentPatronId();
  const showDealerCheckbox = currentPatronId !== 'sda'; // Hide checkbox for 'sda' patron
  
  // For SDA patron, always use manual entry (checkbox is hidden)
  const effectiveUseDealer = showDealerCheckbox ? useDealerForDropLocation : false;
  
  const [formData, setFormData] = useState({
    contactableNumber: contactNumber || '',
    breakdownAddress: '',
    breakdownState: '',
    breakdownCity: '',
    landmark: '',
    serviceType: '',
    serviceSubType: '',
    triageSessionId: '',
    manualLocationReason: '',
    latitude: null,
    longitude: null,
    // Drop location fields for towing services
    dropAddress: '',
    dropState: '',
    dropCity: '',
    dropLandmark: '',
    dropLatitude: null,
    dropLongitude: null,
    selectedDealerId: null,
    // Fuel location fields
    addFuelLocation: false,
    fuelAddress: '',
    fuelState: '',
    fuelCity: '',
    fuelLandmark: '',
    fuelLatitude: null,
    fuelLongitude: null,
    // Scheduling fields
    scheduleForLater: false,
    scheduledAt: ''
  });

  // Fetch service types when component mounts or schemeId changes
  useEffect(() => {
    console.log('CaseServicesSegment: schemeId changed to:', schemeId);
    if (schemeId) {
      fetchServiceTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeId]);

  // Fetch service subtypes when service type changes
  useEffect(() => {
    if (formData.serviceType && formData.serviceType !== '') {
      fetchServiceSubtypes(formData.serviceType);
      
      // If service type is TOW and we have coordinates, fetch dealers
      if (isTowingService() && formData.latitude && formData.longitude) {
        fetchDealers(formData.latitude, formData.longitude);
      }
    } else {
      setServiceSubtypes([]);
      setFormData(prev => ({ ...prev, serviceSubType: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.serviceType]);

  // Fetch dealers when component mounts - REMOVED: will fetch when pickup location is selected

  const fetchServiceTypes = async () => {
    if (!schemeId) return;
    
    setLoadingServiceTypes(true);
    try {
      const response = await runInLane(() => getSchemeServiceTypes(schemeId));
      setServiceTypes(response || []);
      
      // Pass service types to parent component
      if (onServiceTypesLoaded) {
        onServiceTypesLoaded(response || []);
      }
    } catch (error) {
      setServiceTypes([]);
      if (onServiceTypesLoaded) {
        onServiceTypesLoaded([]);
      }
    } finally {
      setLoadingServiceTypes(false);
    }
  };

  const fetchServiceSubtypes = async (serviceTypeId) => {
    console.log('CaseServicesSegment: Fetching service subtypes for serviceTypeId:', serviceTypeId);
    setLoadingServiceSubtypes(true);
    try {
      const response = await getServiceSubtypes(serviceTypeId);
      console.log('CaseServicesSegment: Service subtypes response:', response);
      setServiceSubtypes(response || []);
    } catch (error) {
      console.error('Error fetching service subtypes:', error);
      setServiceSubtypes([]);
    } finally {
      setLoadingServiceSubtypes(false);
    }
  };

  const fetchDealers = async (latitude, longitude) => {
    if (!latitude || !longitude) {
      console.warn('No coordinates provided for dealer search');
      return;
    }
    
    setLoadingDealers(true);
    try {
      const response = await getDealers({ 
        is_active: true, 
        limit: 100,
        latitude: latitude,
        longitude: longitude,
        radius: 100
      });
      setDealers(response.items || []);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      setDealers([]);
    } finally {
      setLoadingDealers(false);
    }
  };

  // Location checking and polling functions
  const checkAndUpdateLocation = useCallback(async () => {
    if (!caseId) return false;
    
    try {
      const result = await checkForLocationUpdate(caseId);
      
      if (result.found && result.addressData) {
        // Update form with the address data
        setFormData(prev => ({
          ...prev,
          breakdownAddress: result.addressData.address,
          breakdownState: result.addressData.state,
          breakdownCity: result.addressData.city,
          latitude: result.addressData.latitude,
          longitude: result.addressData.longitude
        }));
        
        setLocationFound(true);
        setManualAddress(false); // Automatically fill from location
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking location update:', error);
      return false;
    }
  }, [caseId]);

  const stopLocationPolling = useCallback(() => {
    setPollingForLocation(false);
    setPollingTimer(0);
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  const startLocationPolling = useCallback(() => {
    setPollingForLocation(true);
    setPollingTimer(120); // 2 minutes
    setError('');
    
    // Clear any existing intervals/timeouts
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    
    // Set up polling interval (every 10 seconds)
    pollingIntervalRef.current = setInterval(async () => {
      const found = await checkAndUpdateLocation();
      if (found) {
        stopLocationPolling();
        setSuccess('Location received and address updated automatically!');
        setTimeout(() => setSuccess(''), 3000);
      }
    }, 10000);
    
    // Set up timer countdown
    const timerInterval = setInterval(() => {
      setPollingTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          stopLocationPolling();
          setError('Location sharing timeout. Please try again or enter address manually.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Set up 2-minute timeout
    pollingTimeoutRef.current = setTimeout(() => {
      stopLocationPolling();
      setError('Location sharing timeout. Please try again or enter address manually.');
    }, 120000);
  }, [checkAndUpdateLocation, stopLocationPolling]);

  // Check for existing location on form open
  useEffect(() => {
    if (showModal && caseId) {
      // Reset location state when opening form
      setLocationFound(false);
      setPollingForLocation(false);
      
      // Check for existing location update
      checkAndUpdateLocation();
    }
  }, [showModal, caseId, checkAndUpdateLocation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopLocationPolling();
    };
  }, [stopLocationPolling]);

  const handleEditService = (service) => {
    setEditingService(service);
    // Pre-fill form with service data
    setFormData({
      contactableNumber: contactNumber || '',
      breakdownAddress: service.pickup_location?.address || '',
      breakdownState: service.pickup_location?.state || '',
      breakdownCity: service.pickup_location?.city || '',
      landmark: service.pickup_location?.landmark || '',
      serviceType: service.scheme_service_type?.id || '',
      serviceSubType: service.scheme_service_subtype?.id || '',
      manualLocationReason: '',
      latitude: service.pickup_location?.latitude || null,
      longitude: service.pickup_location?.longitude || null,
      // Drop location fields for towing services
      dropAddress: service.drop_location?.address || '',
      dropState: service.drop_location?.state || '',
      dropCity: service.drop_location?.city || '',
      dropLandmark: service.drop_location?.landmark || '',
      dropLatitude: service.drop_location?.latitude || null,
      dropLongitude: service.drop_location?.longitude || null,
      selectedDealerId: null,
      // Fuel location fields
      addFuelLocation: !!service.fuel_location,
      fuelAddress: service.fuel_location?.address || '',
      fuelState: service.fuel_location?.state || '',
      fuelCity: service.fuel_location?.city || '',
      fuelLandmark: service.fuel_location?.landmark || '',
      fuelLatitude: service.fuel_location?.latitude || null,
      fuelLongitude: service.fuel_location?.longitude || null,
      // Scheduling fields
      scheduleForLater: !!service.scheduled_at,
      scheduledAt: service.scheduled_at ? new Date(service.scheduled_at).toISOString().slice(0, 16) : ''
    });
    setManualAddress(true); // Assume manual address for editing
    setUseDealerForDropLocation(false); // Assume manual address for editing
    setShowEditModal(true);
  };

  // Check if address editing is allowed based on service status
  const canEditAddress = (service) => {
    const status = service.status;
    return status === 'pending' || status === 'vendor_not_accepted';
  };

  // Check if current editing service allows address editing
  const canEditCurrentServiceAddress = () => {
    return editingService ? canEditAddress(editingService) : false;
  };

  // Define columns inside component to access handleEditService
  const columns = [
    { key: 'service_number', label: 'Service #', render: (s) => (
      <button 
        onClick={() => handleEditService(s)}
        className="text-blue-700 font-semibold hover:text-blue-900 "
      >
        {s.service_number}
      </button>
    )},
    { key: 'status', label: 'Status' },
    { key: 'service_type', label: 'Service Type', render: (s) => s.scheme_service_type?.service_type_name || '-' },
    { key: 'service_sub_type', label: 'Service Sub Type', render: (s) => s.scheme_service_subtype?.subtype_name || '-' },
    { key: 'vendor', label: 'Vendor', render: (s) => s.vendor_location?.manager?.name || '-' },
    { key: 'driver', label: 'Driver', render: (s) => s.driver?.name || '-' },
    { key: 'scheduled_at', label: 'Scheduled At', render: (s) => s.scheduled_at ? formatDateTime(s.scheduled_at) : '-' },
    { key: 'created_at', label: 'Created At', render: (s) => s.created_at ? formatDateTime(s.created_at) : '-' },
    { key: 'completed_at', label: 'Completed At', render: (s) => s.completed_at ? formatDateTime(s.completed_at) : '-' },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSelect = async (addressData) => {
    setFormData(prev => ({
      ...prev,
      breakdownAddress: addressData.address,
      breakdownState: addressData.state,
      breakdownCity: addressData.city,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    }));
    
    // Fetch dealers when pickup location is selected (for TOW services)
    if (isTowingService() && addressData.latitude && addressData.longitude) {
      fetchDealers(addressData.latitude, addressData.longitude);
    }

    // Recalculate distance if we have a drop location and Google Maps is available
    if (isGoogleMapsLoaded() && formData.dropLatitude && formData.dropLongitude) {
      setDistanceLoading(true);
      setDistanceError('');
      
      try {
        const pickupLocation = {
          latitude: addressData.latitude,
          longitude: addressData.longitude
        };
        const dropLocation = {
          latitude: formData.dropLatitude,
          longitude: formData.dropLongitude,
          address: formData.dropAddress,
          city: formData.dropCity,
          state: formData.dropState
        };
        const distanceResult = await calculatePickupToDropDistance(pickupLocation, dropLocation);
        
        setCalculatedDistance(distanceResult);
        console.log('Distance recalculated after pickup location change:', distanceResult);
      } catch (error) {
        console.error('Error recalculating distance:', error);
        setDistanceError('Failed to recalculate distance. Please try again.');
        setCalculatedDistance(null);
      } finally {
        setDistanceLoading(false);
      }
    }
  };

  const handleDropAddressSelect = async (addressData) => {
    const newDropLocation = {
      address: addressData.address,
      city: addressData.city,
      state: addressData.state,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    };

    setFormData(prev => ({
      ...prev,
      dropAddress: addressData.address,
      dropState: addressData.state,
      dropCity: addressData.city,
      dropLatitude: addressData.latitude,
      dropLongitude: addressData.longitude
    }));

    // Calculate distance if Google Maps is available and we have pickup location
    if (isGoogleMapsLoaded() && formData.latitude && formData.longitude) {
      setDistanceLoading(true);
      setDistanceError('');
      
      try {
        const pickupLocation = {
          latitude: formData.latitude,
          longitude: formData.longitude
        };
        const distanceResult = await calculatePickupToDropDistance(pickupLocation, newDropLocation);
        
        setCalculatedDistance(distanceResult);
        console.log('Distance calculated for new service:', distanceResult);
      } catch (error) {
        console.error('Error calculating distance:', error);
        setDistanceError('Failed to calculate distance. Please try again.');
        setCalculatedDistance(null);
      } finally {
        setDistanceLoading(false);
      }
    }
  };

  const handleFuelAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      fuelAddress: addressData.address,
      fuelState: addressData.state,
      fuelCity: addressData.city,
      fuelLatitude: addressData.latitude,
      fuelLongitude: addressData.longitude
    }));
  };

  const handleDealerSelect = async (dealerId) => {
    const selectedDealer = dealers.find(dealer => dealer.id === parseInt(dealerId));
    if (selectedDealer) {
      const newDropLocation = {
        address: selectedDealer.dealer_name,
        city: selectedDealer.address?.city || '',
        state: selectedDealer.address?.state || '',
        latitude: selectedDealer.address?.latitude || null,
        longitude: selectedDealer.address?.longitude || null
      };

      setFormData(prev => ({
        ...prev,
        dropAddress: selectedDealer.dealer_name,
        dropState: selectedDealer.address?.state || '',
        dropCity: selectedDealer.address?.city || '',
        dropLandmark: selectedDealer.address?.landmark || '',
        dropLatitude: selectedDealer.address?.latitude || null,
        dropLongitude: selectedDealer.address?.longitude || null,
        selectedDealerId: selectedDealer.id
      }));

      // Calculate distance if Google Maps is available and we have pickup location
      if (isGoogleMapsLoaded() && formData.latitude && formData.longitude && newDropLocation.latitude && newDropLocation.longitude) {
        setDistanceLoading(true);
        setDistanceError('');
        
        try {
          const pickupLocation = {
            latitude: formData.latitude,
            longitude: formData.longitude
          };
          const distanceResult = await calculatePickupToDropDistance(pickupLocation, newDropLocation);
          
          setCalculatedDistance(distanceResult);
          console.log('Distance calculated for dealer selection:', distanceResult);
        } catch (error) {
          console.error('Error calculating distance:', error);
          setDistanceError('Failed to calculate distance. Please try again.');
          setCalculatedDistance(null);
        } finally {
          setDistanceLoading(false);
        }
      }
    }
  };

  // Check if the selected service type is TOW
  const isTowingService = () => {
    if (!formData.serviceType) return false;
    const selectedServiceType = serviceTypes.find(st => st.id === parseInt(formData.serviceType));
    return selectedServiceType?.service_type_code === 'TOW';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate service subtype is mandatory
    if (!formData.serviceSubType) {
      setError('Service subtype is mandatory. Please select a service subtype.');
      setLoading(false);
      return;
    }

    // Validate drop location for towing services (now optional)
    if (isTowingService() && effectiveUseDealer && !formData.selectedDealerId) {
      setError('Please select a dealer for drop location.');
      setLoading(false);
      return;
    }

    // Validate fuel location if checkbox is checked
    if (formData.addFuelLocation) {
      if (!formData.fuelAddress) {
        setError('Fuel station address is required when adding fuel location.');
        setLoading(false);
        return;
      }
    }

    // Validate scheduled datetime if scheduling is enabled
    if (formData.scheduleForLater) {
      if (!formData.scheduledAt) {
        setError('Please select a scheduled date and time.');
        setLoading(false);
        return;
      }
      
      // Check if selected datetime is in the future
      const selectedDateTime = new Date(formData.scheduledAt);
      const now = new Date();
      if (selectedDateTime <= now) {
        setError('Scheduled date and time must be in the future.');
        setLoading(false);
        return;
      }
    }

    try {
      // Get the selected service type for description
      const selectedServiceType = serviceTypes.find(st => st.id === parseInt(formData.serviceType));

      const serviceRequestData = {
        case_id: caseId,
        scheme_service_type_id: parseInt(formData.serviceType),
        scheme_service_subtype_id: parseInt(formData.serviceSubType),
        pickup_location: {
          latitude: formData.latitude || -90,
          longitude: formData.longitude || -180,
          address: formData.breakdownAddress,
          landmark: formData.landmark,
          city: formData.breakdownCity,
          state: formData.breakdownState,
          pincode: null
        },
        drop_location: isTowingService() ? (formData.dropAddress ? {
          latitude: formData.dropLatitude || -90,
          longitude: formData.dropLongitude || -180,
          address: formData.dropAddress,
          landmark: formData.dropLandmark,
          city: formData.dropCity || '',
          state: formData.dropState || '',
          pincode: null
        } : null) : {
          latitude: formData.latitude || -90,
          longitude: formData.longitude || -180,
          address: formData.breakdownAddress,
          landmark: formData.landmark,
          city: formData.breakdownCity,
          state: formData.breakdownState,
          pincode: null
        },
        fuel_location: formData.addFuelLocation ? {
          latitude: formData.fuelLatitude || -90,
          longitude: formData.fuelLongitude || -180,
          address: formData.fuelAddress,
          landmark: formData.fuelLandmark,
          city: formData.fuelCity,
          state: formData.fuelState,
          pincode: null
        } : null,
        service_description: `Service for ${selectedServiceType?.service_type_name || 'Unknown'} - Contact: ${formData.contactableNumber}`,
        preferred_vendor_id: null,
        notes: manualAddress ? `Manual location entry. Reason: ${formData.manualLocationReason}` : null,
        scheduled_at: formData.scheduleForLater && formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : null,
        estimated_time_minutes:
          calculatedDistance?.duration > 0 ? Math.round(calculatedDistance.duration) : null,
        // Add calculated distance if available
        distance_km: calculatedDistance && calculatedDistance.distance > 0 ? calculatedDistance.distance : null,
        meta_data: formData.triageSessionId
          ? { triage: { session_id: formData.triageSessionId } }
          : undefined,
      };

      // Check if towing service has no drop location and show confirmation
      if (isTowingService() && !formData.dropAddress && !effectiveUseDealer) {
        setPendingServiceData(serviceRequestData);
        setShowConfirmationModal(true);
        setLoading(false);
        return;
      }

      const response = await createService(serviceRequestData);
      setSuccess('Service created successfully!');
      
      console.log('CaseServicesSegment: Service created successfully:', response.service?.service_number, 'ID:', response.service?.id);
      
      // Call the callback with the new service ID to refresh the segments
      if (onServiceCreated && response.service && response.service.id) {
        console.log('CaseServicesSegment: Calling onServiceCreated with ID:', response.service.id);
        onServiceCreated(response.service.id);
      } else if (onServiceAdded) {
        console.log('CaseServicesSegment: Calling onServiceAdded');
        onServiceAdded();
      }
      
      setTimeout(() => {
        setShowModal(false);
        resetForm();
      }, 2000);
    } catch (err) {
      console.error('Error creating service:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Validate service subtype is mandatory
    if (!formData.serviceSubType) {
      setError('Service subtype is mandatory. Please select a service subtype.');
      setLoading(false);
      return;
    }

    // Validate drop location for towing services (now optional)
    if (isTowingService() && effectiveUseDealer && !formData.selectedDealerId) {
      setError('Please select a dealer for drop location.');
      setLoading(false);
      return;
    }

    // Validate fuel location if checkbox is checked
    if (formData.addFuelLocation) {
      if (!formData.fuelAddress) {
        setError('Fuel station address is required when adding fuel location.');
        setLoading(false);
        return;
      }
    }

    // Validate scheduled datetime if scheduling is enabled
    if (formData.scheduleForLater) {
      if (!formData.scheduledAt) {
        setError('Please select a scheduled date and time.');
        setLoading(false);
        return;
      }
      
      // Check if selected datetime is in the future
      const selectedDateTime = new Date(formData.scheduledAt);
      const now = new Date();
      if (selectedDateTime <= now) {
        setError('Scheduled date and time must be in the future.');
        setLoading(false);
        return;
      }
    }

    try {
      // Get the selected service type for description
      const selectedServiceType = serviceTypes.find(st => st.id === parseInt(formData.serviceType));

      // Prepare update data for the comprehensive service update API
      const updateData = {
        // Service type information
        scheme_service_type_id: parseInt(formData.serviceType),
        scheme_service_subtype_id: parseInt(formData.serviceSubType),
        
        // Service description
        service_description: `Service for ${selectedServiceType?.service_type_name || 'Unknown'} - Contact: ${formData.contactableNumber}`,
        
        // Scheduling information
        scheduled_at: formData.scheduleForLater && formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : null
      };

      // Only include location data if address editing is allowed
      if (canEditCurrentServiceAddress()) {
        updateData.pickup_location = {
          latitude: formData.latitude || -90,
          longitude: formData.longitude || -180,
          address: formData.breakdownAddress,
          landmark: formData.landmark,
          city: formData.breakdownCity,
          state: formData.breakdownState,
          pincode: null
        };
        updateData.drop_location = isTowingService() ? (formData.dropAddress ? {
          latitude: formData.dropLatitude || -90,
          longitude: formData.dropLongitude || -180,
          address: formData.dropAddress,
          landmark: formData.dropLandmark,
          city: formData.dropCity || '',
          state: formData.dropState || '',
          pincode: null
        } : null) : {
          latitude: formData.latitude || -90,
          longitude: formData.longitude || -180,
          address: formData.breakdownAddress,
          landmark: formData.landmark,
          city: formData.breakdownCity,
          state: formData.breakdownState,
          pincode: null
        };
        updateData.fuel_location = formData.addFuelLocation ? {
          latitude: formData.fuelLatitude || -90,
          longitude: formData.fuelLongitude || -180,
          address: formData.fuelAddress,
          landmark: formData.fuelLandmark,
          city: formData.fuelCity,
          state: formData.fuelState,
          pincode: null
        } : null;
        updateData.notes = manualAddress ? `Manual location entry. Reason: ${formData.manualLocationReason}` : null;
      }

      await updateServiceComprehensive(editingService.id, updateData);
      setSuccess('Service updated successfully!');
      
      // Refresh case data and close modal after successful update
      setTimeout(() => {
        if (onServiceAdded) {
          onServiceAdded();
        }
        setShowEditModal(false);
        setEditingService(null);
        resetForm();
      }, 1500);
      
    } catch (err) {
      console.error('Error updating service:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      contactableNumber: contactNumber || '',
      breakdownAddress: '',
      breakdownState: '',
      breakdownCity: '',
      landmark: '',
      serviceType: '',
      serviceSubType: '',
      manualLocationReason: '',
      latitude: null,
      longitude: null,
      // Drop location fields for towing services
      dropAddress: '',
      dropState: '',
      dropCity: '',
      dropLandmark: '',
      dropLatitude: null,
      dropLongitude: null,
      selectedDealerId: null,
      // Fuel location fields
      addFuelLocation: false,
      fuelAddress: '',
      fuelState: '',
      fuelCity: '',
      fuelLandmark: '',
      fuelLatitude: null,
      fuelLongitude: null,
      // Scheduling fields
      scheduleForLater: false,
      scheduledAt: ''
    });
    setManualAddress(false);
    setUseDealerForDropLocation(false); // Default to manual entry
    setError('');
    setSuccess('');
    setLocationFound(false);
    stopLocationPolling();
    // Clear distance calculation
    setCalculatedDistance(null);
    setDistanceError('');
  };

  const handleCancel = () => {
    setShowModal(false);
    setShowEditModal(false);
    setShowConfirmationModal(false);
    setEditingService(null);
    setPendingServiceData(null);
    resetForm();
  };

  const handleConfirmServiceCreation = async () => {
    if (!pendingServiceData) return;
    
    setLoading(true);
    setShowConfirmationModal(false);
    
    try {
      const response = await createService(pendingServiceData);
      setSuccess('Service created successfully!');
      
      console.log('CaseServicesSegment: Service created successfully:', response.service?.service_number, 'ID:', response.service?.id);
      
      // Call the callback with the new service ID to refresh the segments
      if (onServiceCreated && response.service && response.service.id) {
        console.log('CaseServicesSegment: Calling onServiceCreated with ID:', response.service.id);
        onServiceCreated(response.service.id);
      } else if (onServiceAdded) {
        console.log('CaseServicesSegment: Calling onServiceAdded');
        onServiceAdded();
      }
      
      setTimeout(() => {
        setShowModal(false);
        setPendingServiceData(null);
        resetForm();
      }, 2000);
    } catch (err) {
      console.error('Error creating service:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocationFromWhatsApp = async () => {
    if (!formData.contactableNumber) {
      setError('Contact number is required to send WhatsApp message');
      return;
    }

    // First check if location already exists
    const existingLocation = await checkAndUpdateLocation();
    if (existingLocation) {
      setSuccess('Location already available and address updated!');
      return;
    }

    setSendingWhatsApp(true);
    setError('');

    try {
      // Create the location sharing URL using current domain
      const currentDomain = window.location.origin;
      const locationUrl = `${currentDomain}/share-my-location?phone=${encodeURIComponent(formData.contactableNumber)}&type=c&caseId=${caseId}`;
      
      // Get customer name from the case data
      const customerName = caseObj?.customer?.name || 'Customer';
      
      const requestData = {
        mobile_number: formData.contactableNumber,
        user_name: customerName,
        customer_name: customerName,
        location_link: locationUrl
      };

      const response = await sendLocationSharingMessage(requestData);
      
      if (response.success) {
        setSuccess('WhatsApp message sent! Waiting for customer location...');
        // Start polling for location updates
        startLocationPolling();
      } else {
        setError('Failed to send WhatsApp message');
      }
    } catch (err) {
      console.error('Error sending WhatsApp message:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleServiceSelection = (service) => {
    console.log("CaseServicesSegment: Service selected:", service);
    // Call the original onSelectService to update parent state
    onSelectService(service);
    
    // Remove the navigation that causes page reload - just update state
    // The parent component will handle fetching detailed service data
  };

  const renderForm = (isEdit = false) => (
    <form onSubmit={isEdit ? handleEditSubmit : handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-sm text-black mb-1">
            Contactable Number<span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="contactableNumber"
            value={formData.contactableNumber}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>

        {schemeId && (
          <div className="md:col-span-2 mb-2">
            <TriagePanel
              schemeId={schemeId}
              onOutcome={(res) => {
                const pre = res.outcome?.prefill || {};
                setFormData((prev) => ({
                  ...prev,
                  triageSessionId: res.sessionId,
                  serviceType: pre.scheme_service_type_id ? String(pre.scheme_service_type_id) : prev.serviceType,
                  serviceSubType: pre.scheme_service_subtype_id ? String(pre.scheme_service_subtype_id) : prev.serviceSubType,
                }));
              }}
            />
          </div>
        )}
        <div className='regit_bform '>
          <label className="block text-sm font-sm text-black mb-1">
            Service Type<span className="text-red-500">*</span>
          </label>
          <select
            name="serviceType"
            value={formData.serviceType}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-gray-500 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            disabled={loading || loadingServiceTypes}
          >
            <option value="">- Select Service Type -</option>
            {loadingServiceTypes ? (
              <option disabled>Loading service types...</option>
            ) : serviceTypes.length === 0 ? (
              <option disabled>No service types available for this scheme</option>
            ) : (
              serviceTypes.map(serviceType => (
                <option key={serviceType.id} value={serviceType.id}>
                  {serviceType.service_type_name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className='regit_bform '>
          <label className="block text-sm font-sm text-black mb-1">
            Service Subtype<span className="text-red-500">*</span>
          </label>
          <select
            name="serviceSubType"
            value={formData.serviceSubType}
            onChange={handleInputChange}
            className="w-full px-3 py-2 font-normal text-gray-500 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading || loadingServiceSubtypes || !formData.serviceType}
            required
          >
            <option value="">- Select Service Subtype -</option>
            {loadingServiceSubtypes ? (
              <option disabled>Loading service subtypes...</option>
            ) : serviceSubtypes.length === 0 ? (
              <option disabled>No service subtypes available for this service type</option>
            ) : (
              serviceSubtypes.map(subtype => (
                <option key={subtype.id} value={subtype.id}>
                  {subtype.subtype_name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Scheduling Section */}
      <div className="border-t pt-6">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="scheduleForLater"
            name="scheduleForLater"
            checked={formData.scheduleForLater}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="scheduleForLater" className="ml-2 block text-sm font-medium text-black">
            Schedule for later
          </label>
        </div>
        
        {formData.scheduleForLater && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className='regit_bform'>
              <label className="block text-sm font-sm text-black mb-1">
                Scheduled Date & Time (IST)<span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="scheduledAt"
                value={formData.scheduledAt}
                onChange={handleInputChange}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 text-gray-500 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={formData.scheduleForLater}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Select when you want the service to start
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="manualAddress"
            checked={manualAddress}
            onChange={(e) => setManualAddress(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500  border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="manualAddress" className="ml-2 block text-sm text-black">
            Manual Address Entry
          </label>
        </div>
        
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleGetLocationFromWhatsApp}
            disabled={loading || sendingWhatsApp || pollingForLocation || !formData.contactableNumber}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {sendingWhatsApp ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : pollingForLocation ? (
              <>
                <svg className="animate-pulse -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Waiting... {Math.floor(pollingTimer / 60)}:{(pollingTimer % 60).toString().padStart(2, '0')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Get Location from WhatsApp
              </>
            )}
          </button>
          
          {pollingForLocation && (
            <button
              type="button"
              onClick={stopLocationPolling}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 flex items-center"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {manualAddress && (
        <div className='regit_bform '>
          <label className="block text-sm font-sm text-black mb-1">
            Reason for Manual Location<span className="text-red-500">*</span>
          </label>
          <select
            name="manualLocationReason"
            value={formData.manualLocationReason}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border text-gray-500 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            disabled={loading}
          >
            {MANUAL_LOCATION_REASONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm mb-1">
          Breakdown Address<span className="text-red-500">*</span>
          {locationFound && (
            <span className="ml-2 text-green-600 text-xs font-medium">✓ Location Updated</span>
          )}
        </label>
        {manualAddress ? (
          <AddressAutocomplete
            value={formData.breakdownAddress}
            onChange={(value) => setFormData(prev => ({ ...prev, breakdownAddress: value }))}
            onAddressSelect={handleAddressSelect}
            placeholder="Search for address in New Zealand..."
            disabled={loading}
          />
        ) : (
          <div className="space-y-2">
            <input
              type="text"
              value={formData.breakdownAddress || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, breakdownAddress: e.target.value }))}
              className={`w-full px-3 py-2 text-gray-500 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                !formData.breakdownAddress && !pollingForLocation ? 'bg-gray-100 text-gray-500' : ''
              }`}
              disabled={pollingForLocation || (!formData.breakdownAddress && !locationFound)}
              placeholder={
                pollingForLocation 
                  ? "Waiting for customer location..." 
                  : locationFound 
                    ? "Address from customer location"
                    : "Address will be filled automatically"
              }
            />
            {pollingForLocation && (
              <p className="text-sm text-blue-600 flex items-center">
                <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Waiting for customer to share location...
              </p>
            )}
            {locationFound && !pollingForLocation && (
              <p className="text-sm text-green-600">✓ Address updated from customer's shared location</p>
            )}
            {!locationFound && !pollingForLocation && !formData.breakdownAddress && (
              <p className="text-sm text-gray-500">Use "Get Location from WhatsApp" or enable manual entry</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-sm text-black mb-1">
            Breakdown State<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="breakdownState"
            value={formData.breakdownState || ''}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border text-1x1 text-gray-500 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !manualAddress && !locationFound ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
            }`}
            disabled={loading || (!manualAddress && !locationFound) || pollingForLocation}
            required={manualAddress || locationFound}
            placeholder={
              manualAddress 
                ? "Auto-filled from address search" 
                : locationFound
                  ? "From customer location"
                  : "Will be filled automatically"
            }
          />
        </div>

        <div>
          <label className="block text-sm font-sm text-black mb-1">
            Breakdown City<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="breakdownCity"
            value={formData.breakdownCity || ''}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !manualAddress && !locationFound ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
            }`}
            disabled={loading || (!manualAddress && !locationFound) || pollingForLocation}
            required={manualAddress || locationFound}
            placeholder={
              manualAddress 
                ? "Auto-filled from address search" 
                : locationFound
                  ? "From customer location"
                  : "Will be filled automatically"
            }
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-sm text-black mb-1">
          Landmark
        </label>
        <input
          type="text"
          name="landmark"
          value={formData.landmark}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border text-gray-500 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading}
          placeholder="Enter landmark (optional)..."
        />
      </div>

      {/* Drop Location Fields - Only for Towing Services */}
      {isTowingService() && (
        <>
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Drop Location (Towing Destination)</h3>
            
            {/* Dealer Selection Checkbox - Only show for non-SDA patrons */}
            {showDealerCheckbox && (
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="useDealerForDropLocation"
                  checked={useDealerForDropLocation}
                  onChange={(e) => setUseDealerForDropLocation(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="useDealerForDropLocation" className="ml-2 block text-sm text-black">
                  Select from existing dealers
                </label>
              </div>
            )}
            
            {effectiveUseDealer ? (
              /* Dealer Dropdown */
              <div>
                <label className="block text-sm mb-1">
                  Select Dealer<span className="text-red-500">*</span>
                </label>
                <select
                  name="selectedDealerId"
                  value={formData.selectedDealerId || ''}
                  onChange={(e) => handleDealerSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loading || loadingDealers}
                >
                  <option value="">- Select Dealer -</option>
                  {loadingDealers ? (
                    <option disabled>Loading dealers...</option>
                  ) : dealers.length === 0 ? (
                    <option disabled>
                      {isTowingService() && formData.latitude && formData.longitude 
                        ? 'No dealers found within 100km radius' 
                        : 'Select pickup location first to load nearby dealers'}
                    </option>
                  ) : (
                    dealers.map(dealer => (
                      <option key={dealer.id} value={dealer.id}>
                        {dealer.dealer_name} - {dealer.dealer_type} ({dealer.contact_number})
                        {dealer.distance_km ? ` - ${dealer.distance_km}km away` : ''}
                      </option>
                    ))
                  )}
                </select>
                {formData.selectedDealerId && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                      <strong>Selected Dealer:</strong> {dealers.find(d => d.id === formData.selectedDealerId)?.dealer_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Type:</strong> {dealers.find(d => d.id === formData.selectedDealerId)?.dealer_type}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Contact:</strong> {dealers.find(d => d.id === formData.selectedDealerId)?.contact_number}
                    </p>
                    {dealers.find(d => d.id === formData.selectedDealerId)?.distance_km && (
                      <p className="text-sm text-gray-600">
                        <strong>Distance:</strong> {dealers.find(d => d.id === formData.selectedDealerId)?.distance_km}km from pickup location
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Manual Address Input */
              <div>
                <label className="block text-sm mb-1">
                  Drop Address
                </label>
                <AddressAutocomplete
                  value={formData.dropAddress}
                  onChange={(value) => setFormData(prev => ({ ...prev, dropAddress: value }))}
                  onAddressSelect={handleDropAddressSelect}
                  placeholder="Search for drop location address in New Zealand... (optional)"
                  disabled={loading}
                />
              </div>
            )}



            <div>
              <label className="block text-sm font-sm text-black mb-1">
                Drop Landmark
              </label>
              <input
                type="text"
                name="dropLandmark"
                value={formData.dropLandmark}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border text-gray-500 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
                placeholder="Enter drop location landmark (optional)..."
              />
            </div>

            {/* Distance calculation display */}
            {distanceLoading && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="animate-spin h-5 w-5 text-blue-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-blue-800 font-medium text-sm">Calculating distance...</span>
                </div>
              </div>
            )}
            
            {distanceError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium text-sm">{distanceError}</span>
                </div>
              </div>
            )}
            
            {calculatedDistance && !distanceLoading && !distanceError && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-800 font-medium text-sm">
                      Distance: {calculatedDistance.distance} km
                    </span>
                  </div>
                  <span className="text-green-600 text-xs">
                    This distance will be saved with the service
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Fuel Location Section */}
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="addFuelLocation"
            checked={formData.addFuelLocation}
            onChange={(e) => setFormData(prev => ({ ...prev, addFuelLocation: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="addFuelLocation" className="ml-2 block text-sm font-medium text-gray-900">
            Add Fuel Location on the way
          </label>
        </div>
        
        {formData.addFuelLocation && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-1">
                Fuel Station Address<span className="text-red-500">*</span>
              </label>
              <AddressAutocomplete
                value={formData.fuelAddress}
                onChange={(value) => setFormData(prev => ({ ...prev, fuelAddress: value }))}
                onAddressSelect={handleFuelAddressSelect}
                placeholder="Search for fuel station address in New Zealand..."
                disabled={loading}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4 pt-3">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEdit ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            isEdit ? 'Update' : 'Save'
          )}
        </button>
      </div>
    </form>
  );

  // Render read-only form for services that don't allow address editing
  const renderReadOnlyForm = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-yellow-800 font-medium">
            Service editing is restricted for services with status "{editingService?.status}". This service cannot be modified.
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contactable Number
          </label>
          <input
            type="tel"
            value={formData.contactableNumber}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Type
          </label>
          <input
            type="text"
            value={detailedServiceData?.service?.scheme_service_type?.service_type_name || editingService?.scheme_service_type?.service_type_name || 'N/A'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Subtype
          </label>
          <input
            type="text"
            value={detailedServiceData?.service?.scheme_service_subtype?.subtype_name || editingService?.scheme_service_subtype?.subtype_name || 'N/A'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>
      </div>

      {/* Read-only address fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Breakdown Address
        </label>
        <input
          type="text"
          value={detailedServiceData?.service?.pickup_location?.address || editingService?.pickup_location?.address || 'N/A'}
          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Breakdown State
          </label>
          <input
            type="text"
            value={detailedServiceData?.service?.pickup_location?.state || editingService?.pickup_location?.state || 'N/A'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Breakdown City
          </label>
          <input
            type="text"
            value={detailedServiceData?.service?.pickup_location?.city || editingService?.pickup_location?.city || 'N/A'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
            disabled
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Landmark
        </label>
        <input
          type="text"
          value={detailedServiceData?.service?.pickup_location?.landmark || editingService?.pickup_location?.landmark || 'N/A'}
          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
          disabled
        />
      </div>

      {/* Read-only fuel location fields */}
      {(detailedServiceData?.service?.fuel_location || editingService?.fuel_location) && (
        <>
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Fuel Location</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuel Station Address
              </label>
              <input
                type="text"
                value={detailedServiceData?.service?.fuel_location?.address || editingService?.fuel_location?.address || 'N/A'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                disabled
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-4 pt-6 border-t">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          disabled={loading}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Section
        icon={<ListTree className="h-3.5 w-3.5" strokeWidth={2} />}
        title="Services on this case"
        subtitle={caseObj?.case?.case_number ? `All dispatch jobs linked to ${caseObj.case.case_number}` : 'All dispatch jobs on this case'}
        dense
        action={
          <button
            type="button"
            className="cwp-btn-primary inline-flex h-8 items-center gap-1.5 px-3 text-[12.5px]"
            onClick={() => setShowModal(true)}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add service
          </button>
        }
      >
        <div
          className="overflow-hidden rounded-lg cwp-scrollbar-thin"
          style={{ border: '1px solid var(--cwp-border)' }}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="text-left" style={{ background: 'var(--cwp-card-muted)' }}>
                  <th className="whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500" aria-label="Selected" />
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingServices ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                      <span className="inline-flex items-center justify-center gap-2">
                        <svg
                          className="h-6 w-6 animate-spin text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Loading services…
                      </span>
                    </td>
                  </tr>
                ) : services.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                      No services found
                    </td>
                  </tr>
                ) : (
                  services.map((s, idx) => {
                    const isSel = selectedService && selectedService.id === s.id;
                    return (
                      <tr
                        key={s.id || idx}
                        className={`border-t border-slate-200/90 transition-colors ${isSel ? 'bg-blue-50/70' : 'hover:bg-slate-50/80'}`}
                        style={{ borderTop: '1px solid var(--cwp-border)' }}
                      >
                        <td className="whitespace-nowrap px-3 py-2.5 align-middle">
                          <label className="inline-flex cursor-pointer items-center gap-2">
                            <input
                              type="radio"
                              name="selectService"
                              className="sr-only"
                              checked={Boolean(isSel)}
                              onChange={() => handleServiceSelection(s)}
                            />
                            <span
                              className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-offset-1 ${
                                isSel ? 'ring-blue-500/40' : 'ring-slate-200'
                              }`}
                              style={{
                                background: isSel
                                  ? 'linear-gradient(135deg, oklch(0.58 0.2 262), oklch(0.5 0.22 280))'
                                  : 'var(--cwp-card-muted)',
                                boxShadow: isSel ? '0 0 0 3px oklch(0.62 0.18 262 / 0.2)' : undefined,
                              }}
                              aria-hidden
                            >
                              {isSel ? <span className="h-1 w-1 rounded-full bg-white" /> : null}
                            </span>
                          </label>
                        </td>
                        {columns.map((col) => (
                          <td key={col.key} className="whitespace-nowrap px-3 py-2.5 text-slate-700">
                            {col.render ? col.render(s) : s[col.key] || '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* Add Service Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-drk-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg  max-w-2xl w-full mx-4 pb-2">
          <div className="flex items-center p-6 pt-4 pb-4 justify-between mb-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add New Service</h3>
              <button
                onClick={handleCancel}
                className="bg-red-500 text-white rounded py-1 px-1 hover:text-white focus:outline-none"
                disabled={loading}
              >
                 <X className="w-5 h-5" />
              </button>
            </div>
            <div className='pr-3'>
              
              <div className='max-h-[60vh] py-6 pl-6 pr-3 pt-0 pb-3 overflow-y-auto'>
            
            
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-800 font-medium">{success}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              </div>
            )}
            
            {renderForm(false)}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {canEditCurrentServiceAddress() ? 'Edit Service' : 'View Service Details'} - {editingService?.service_number}
              </h3>
              <button
                onClick={handleCancel}
                className="bg-red-500 text-white rounded py-1 px-1 text-2xl hover:text-white focus:outline-none"
                disabled={loading}
              >
                 <X className="w-5 h-5" />
              </button>
            </div>
            
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-800 font-medium">{success}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              </div>
            )}
            
            {canEditCurrentServiceAddress() ? renderForm(true) : renderReadOnlyForm()}
          </div>
        </div>
      )}

      {/* Confirmation Modal for Towing Service without Drop Location */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <svg className="h-6 w-6 text-yellow-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Missing Drop Location</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              You are creating a towing service without specifying a drop location. 
              The service can be created, but you'll need to add the drop location later for the service to be complete.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setPendingServiceData(null);
                  setLoading(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmServiceCreation}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default memo(CaseServicesSegment); 